import React, { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/firebaseServices';
import './blogcss/blog.css';

// Local images
import dvtSymptomsImage from './blogimg/DVT/dvt_symptoms.jpg';
import naturalSupplementsImage from './blogimg/DVT/natural_supplements.jpg';
import dvtImage from './blogimg/DVT/dvt.jpg';
import riskFactorsImage from './blogimg/DVT/risk_factors.jpg';
import lifestyleChangesImage from './blogimg/DVT/lifestyle_changes.jpg';
import highRiskDvtImage from './blogimg/DVT/high_risk_dvt.jpg';
import medicalAttentionImage from './blogimg/DVT/medical_attention.jpg';
import healthyLivingImage from './blogimg/DVT/healthy_living.jpg';

function getLocalImageSource(filename) {
  const commaIndex = filename.indexOf(',');
  if (commaIndex !== -1) {
    filename = filename.substring(0, commaIndex);
  }
  const parts = filename.split(/[/\\]/);
  const baseName = parts[parts.length - 1].trim().toLowerCase();

  switch (baseName) {
    case 'dvt_symptoms.jpg':
      return dvtSymptomsImage;
    case 'natural_supplements.jpg':
      return naturalSupplementsImage;
    case 'dvt.jpg':
      return dvtImage;
    case 'risk_factors.jpg':
      return riskFactorsImage;
    case 'lifestyle_changes.jpg':
      return lifestyleChangesImage;
    case 'high_risk_dvt.jpg':
      return highRiskDvtImage;
    case 'medical_attention.jpg':
      return medicalAttentionImage;
    case 'healthy_living.jpg':
      return healthyLivingImage;
    default:
      return null;
  }
}
const DeepVeinThrombosisPreventionPage = () => {
  const [article, setArticle] = useState(null);
  const [timestamp, setTimestamp] = useState(null);

  // 1️⃣ Add Meta Tags Dynamically
  useEffect(() => {
    // Set the document title
    document.title = "Deep Vein Thrombosis - How to Prevent It Naturally with Supplements & Lifestyle Tips";

    // Helper function to add or update meta tags
    const setMetaTag = (attrName, contentValue, useProperty = false) => {
      const key = useProperty ? 'property' : 'name';
      let metaTag = document.querySelector(`meta[${key}="${attrName}"]`);

      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute(key, attrName);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', contentValue);
    };

    // Standard meta tags
    setMetaTag(
      "description",
      "Learn how to prevent Deep Vein Thrombosis (DVT) naturally with supplements and lifestyle changes. Discover risk factors, symptoms, and prevention strategies."
    );
    setMetaTag(
      "keywords",
      "Deep Vein Thrombosis, DVT prevention, blood clot prevention, natural supplements for circulation, lifestyle changes for DVT, Pure Health Nexus"
    );

    // Open Graph tags
    setMetaTag(
      "og:title",
      "Deep Vein Thrombosis: How to Prevent It Naturally with Supplements & Lifestyle Tips",
      true
    );
    setMetaTag(
      "og:description",
      "Discover effective natural ways to prevent Deep Vein Thrombosis (DVT) with supplements and lifestyle changes. Learn about risk factors, symptoms, and prevention strategies.",
      true
    );
    setMetaTag(
      "og:image",
      "https://purehealthnexus.com/risk_factors.jpg",
      true
    );
    setMetaTag(
      "og:url",
      "https://purehealthnexus.com/deep-vein-thrombosis-how-to-prevent-it-naturally-with-supplements-&-lifestyle-tips",
      true
    );

    // Twitter Card tags (optional but recommended)
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", "Deep Vein Thrombosis: How to Prevent It Naturally");
    setMetaTag("twitter:description", "Discover natural ways to prevent Deep Vein Thrombosis (DVT) with supplements and lifestyle changes. Learn about risk factors, symptoms, and prevention strategies.");
    setMetaTag("twitter:image", "https://purehealthnexus.com/risk_factors.jpg");

    // Cleanup on unmount (optional)
    return () => {
      document.title = "Pure Health Nexus";
    };
  }, []);

  // 2️⃣ Fetch the Article from Firestore
  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const colRef = collection(
          db,
          'products',
          'blog',
          'deepVeinThrombosisPrevention'
        );
        const snapshot = await getDocs(colRef);

        if (!snapshot.empty) {
          const docData = snapshot.docs[0].data();
          setArticle(docData);
          setTimestamp(docData.eutimestamp);
        } else {
        }
      } catch (error) {
      }
    };

    fetchArticle();
  }, []);

  if (!article) {
    return <div>Loading...</div>;
  }

  // 3️⃣ Organize Sections for Conclusion & References
  let conclusionSection = null;
  let referenceBlock = null;
  let otherBlocks = [];

  if (article.sections && article.sections.length > 0) {
    article.sections.forEach((section, sectionIndex) => {
      if (section.blocks && section.blocks.length > 0) {
        // If the first block is an h2 titled "Conclusion", set as conclusion
        if (section.blocks[0].tag === 'h2' && section.blocks[0].text === 'Conclusion') {
          conclusionSection = section;
        }
        // If it's the last section, set as references
        else if (sectionIndex === article.sections.length - 1) {
          referenceBlock = section.blocks;
        } else {
          otherBlocks.push(section);
        }
      }
    });
  }

  // 4️⃣ Render Block Function
  const renderBlock = (block, index, isReference = false) => {
    const { tag, text } = block;
    const style = isReference ? { fontSize: '10px', margin: '0' } : {};

    switch (tag) {
      case 'h1':
        return (
          <h1 key={index} className="recipe-h1" style={style}>
            {text}
            <style>{`.recipe-h1::after { left: 8%; }`}</style>
          </h1>
        );
      case 'h2':
        return (
          <React.Fragment key={index}>
            <hr
              style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
            />
            <h2 className="recipe-h2" style={style}>
              <span>{text}</span>
            </h2>
          </React.Fragment>
        );
      case 'h3':
        return (
          <React.Fragment key={index}>
            <hr
              style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
            />
            <h3 className="recipe-h3" style={style}>
              {text}
            </h3>
          </React.Fragment>
        );
      case 'p':
        return (
          <p key={index} className="recipe-p" style={style}>
            {text}
          </p>
        );
      case 'img': {
        const localImg = getLocalImageSource(text);
        return (
          <div key={index} className="recipe-img">
            <img
              src={localImg ?? text}
              alt={text}
              style={{ maxHeight: '500px', width: 'auto' }}
            />
          </div>
        );
      }
      case 'ul':
        return (
          <ul key={index} className="recipe-ul" style={style}>
            {Array.isArray(text)
              ? text.map((li, liIndex) => <li key={liIndex}>{li}</li>)
              : <li>{text}</li>}
          </ul>
        );
      case 'ol':
        return (
          <ol key={index} className="recipe-ol" style={style}>
            {Array.isArray(text) &&
              text.map((li, liIndex) => {
                if (typeof li === 'object') {
                  return (
                    <li key={liIndex}>
                      {li.text}
                      {li.nested && (
                        <ul>
                          {li.nested.map((nestedItem, nestedIndex) => (
                            <li key={nestedIndex}>{nestedItem}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                } else {
                  return <li key={liIndex}>{li}</li>;
                }
              })}
          </ol>
        );
      case 'hr':
        return (
          <hr
            key={index}
            style={{ width: '75%', margin: '20px auto', border: '1px solid #e67e00' }}
          />
        );
      default:
        return (
          <div key={index} style={style}>
            {text}
          </div>
        );
    }
  };
  // Format the timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };
  // 5️⃣ Render the Page
  return (
    <div className="container">
      <div className="calming-morning-routine">
        {/* Main H1 */}
        {article.h1 && (
          <h1 className="recipe-h1" style={{ position: 'relative' }}>
            {article.h1}
            <style>{`.recipe-h1::after { left: 8%; }`}</style>
          </h1>
        )}
        {timestamp && (
          <p className="timestamp" style={{ textAlign: 'center', color: '#888' }}>
            Updated: {formatTimestamp(timestamp)}
          </p>
        )}
        {/* Render Other Blocks (excluding Conclusion & References) */}
        {otherBlocks.map((section, sectionIndex) => (
          <section key={sectionIndex}>
            {section.blocks &&
              section.blocks.map((block, blockIndex) => renderBlock(block, blockIndex))}
          </section>
        ))}

        {/* Conclusion Section */}
        {conclusionSection && (
          <section>
            {conclusionSection.blocks.map((block, index) => renderBlock(block, index))}
          </section>
        )}

        {/* CTA Button */}
        <button
          className="recipe-button"
          onClick={() => (window.location.href = 'https://purehealthnexus.com/top-rated-vitamins')}
        >
          See Top-Rated Vitamins Here
        </button>

        {/* References Section after Button */}
        {referenceBlock &&
          referenceBlock.map((block, index) => (
            <div className="notranslate" key={index}>
              {renderBlock(block, index, true)}
            </div>
          ))}
        </div>
    </div>
  );
};

export default DeepVeinThrombosisPreventionPage;
